import { useRef } from 'react'
import { Autocomplete } from '@react-google-maps/api'
import { MapPin } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { useGoogleMapsLoader } from '@/hooks/use-google-maps-loader'
import { getGooglePlaceCity } from '@/lib/map-location'
import { cn } from '@/lib/utils'

export type JobLocationPlace = {
  label: string
  city: string
}

type JobLocationSearchFieldProps = {
  id: string
  value: string
  onValueChange: (value: string) => void
  onPlaceSelected: (place: JobLocationPlace) => void
  serviceInstanceId?: string | number | null
  invalid?: boolean
  describedBy?: string
}

/**
 * Address search for job forms. Uses the same Google Maps loader and Places
 * Autocomplete as the New Booking (POS) address search, without a map preview.
 */
export function JobLocationSearchField({
  id,
  value,
  onValueChange,
  onPlaceSelected,
  serviceInstanceId,
  invalid = false,
  describedBy,
}: JobLocationSearchFieldProps) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const {
    mapsAvailable,
    status: mapsLoaderStatus,
    statusMessage: mapsStatusMessage,
  } = useGoogleMapsLoader({ serviceInstanceReference: serviceInstanceId })

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace()
    if (!place) {
      return
    }

    const label = String(place.formatted_address || place.name || '').trim()
    if (!label) {
      return
    }

    onValueChange(label)
    onPlaceSelected({ label, city: getGooglePlaceCity(place) })
  }

  const input = (
    <div className="relative">
      <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input
        id={id}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        placeholder={mapsAvailable ? 'Search address, postcode or place' : 'Enter location'}
        autoComplete="off"
        aria-invalid={invalid}
        aria-describedby={describedBy}
        disabled={mapsLoaderStatus === 'loading-key'}
        className={cn('pl-9')}
      />
    </div>
  )

  return (
    <div className="space-y-1.5">
      {mapsAvailable ? (
        <Autocomplete
          onLoad={(autocomplete) => {
            autocompleteRef.current = autocomplete
          }}
          onPlaceChanged={onPlaceChanged}
        >
          {input}
        </Autocomplete>
      ) : input}
      {!mapsAvailable && mapsStatusMessage ? (
        <p className="text-xs text-muted-foreground">{mapsStatusMessage}</p>
      ) : null}
    </div>
  )
}
